import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);

  const languages = [
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
    { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾' },
    { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  ];

  const currentLang = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
      if (isMobileMenuOpen && navRef.current && !navRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLangOpen(false);
    setIsMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav ref={navRef} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || isMobileMenuOpen ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://static.readdy.ai/image/c1d369de78ba6b6a884b69ca21dbc9f2/81a4a68f1cccee589378da1e71db4154.png" 
                alt="泰山乐器" 
                className="h-12 w-auto"
              />
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              <button 
                onClick={() => scrollToSection('home')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-800 hover:text-amber-600' : 'text-white hover:text-amber-300'}`}
              >
                {t('nav_home')}
              </button>
              <button 
                onClick={() => scrollToSection('products')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-800 hover:text-amber-600' : 'text-white hover:text-amber-300'}`}
              >
                {t('nav_products')}
              </button>
              <button 
                onClick={() => scrollToSection('about')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-800 hover:text-amber-600' : 'text-white hover:text-amber-300'}`}
              >
                {t('nav_about')}
              </button>
              <button 
                onClick={() => scrollToSection('support')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-800 hover:text-amber-600' : 'text-white hover:text-amber-300'}`}
              >
                {t('nav_support')}
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-800 hover:text-amber-600' : 'text-white hover:text-amber-300'}`}
              >
                {t('nav_contact')}
              </button>
              
              {/* Language Switcher */}
              <div className="relative" ref={langRef}>
                <button
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                    isScrolled 
                      ? 'text-gray-800 hover:bg-gray-100' 
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  <span className="text-lg">{currentLang.flag}</span>
                  <span className="text-sm font-medium">{currentLang.name}</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${isLangOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {isLangOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full flex items-center space-x-3 px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                          i18n.language === lang.code
                            ? 'bg-amber-50 text-amber-600'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="font-medium">{lang.name}</span>
                        {i18n.language === lang.code && (
                          <i className="ri-check-line ml-auto text-amber-600"></i>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                isScrolled || isMobileMenuOpen
                  ? 'text-gray-800 hover:bg-gray-100' 
                  : 'text-white hover:bg-white/10'
              }`}
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-gray-200">
              <div className="flex flex-col space-y-3 pt-4">
                <button 
                  onClick={() => scrollToSection('home')}
                  className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-left px-4 py-2 rounded-lg text-gray-800 hover:bg-gray-100"
                >
                  {t('nav_home')}
                </button>
                <button 
                  onClick={() => scrollToSection('products')}
                  className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-left px-4 py-2 rounded-lg text-gray-800 hover:bg-gray-100"
                >
                  {t('nav_products')}
                </button>
                <button 
                  onClick={() => scrollToSection('about')}
                  className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-left px-4 py-2 rounded-lg text-gray-800 hover:bg-gray-100"
                >
                  {t('nav_about')}
                </button>
                <button 
                  onClick={() => scrollToSection('support')}
                  className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-left px-4 py-2 rounded-lg text-gray-800 hover:bg-gray-100"
                >
                  {t('nav_support')}
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-left px-4 py-2 rounded-lg text-gray-800 hover:bg-gray-100"
                >
                  {t('nav_contact')}
                </button>
                
                {/* Mobile Language Switcher */}
                <div className="px-4 pt-2">
                  <div className="text-xs font-medium mb-2 text-gray-600">
                    {t('nav_language') || '语言 / Language'}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                          i18n.language === lang.code
                            ? 'bg-amber-600 text-white'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        <span className="text-base">{lang.flag}</span>
                        <span className="font-medium text-xs">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/fbb3c48ed12836724f4bf986946a04da.jpg" 
            alt="泰山乐器" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center px-6 w-full">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {t('hero_title')}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-4">
            {t('hero_subtitle')}
          </p>
          <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            {t('hero_description')}
          </p>
          <button 
            onClick={() => scrollToSection('products')}
            className="bg-amber-600 text-white px-8 py-3 rounded-lg text-base font-medium hover:bg-amber-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('hero_cta')}
          </button>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('products_title')}</h2>
            <p className="text-lg text-gray-600">{t('products_subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Trumpet */}
            <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-full h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20golden%20brass%20trumpet%20instrument%20displayed%20on%20a%20clean%20white%20background%20with%20soft%20studio%20lighting%20highlighting%20the%20polished%20metal%20surface%20and%20intricate%20valve%20details%20product%20photography%20style&width=600&height=400&seq=prod001&orientation=landscape" 
                  alt={t('product_trumpet')} 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('product_trumpet')}</h3>
                <p className="text-sm text-gray-600 mb-4">{t('product_trumpet_desc')}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-amber-600 font-medium">{t('view_details')}</span>
                  <i className="ri-arrow-right-line text-amber-600"></i>
                </div>
              </div>
            </div>

            {/* Trombone */}
            <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-full h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20brass%20trombone%20with%20slide%20extended%20on%20a%20clean%20white%20background%20studio%20lighting%20showcasing%20the%20golden%20finish%20and%20craftsmanship%20details%20product%20photography%20style&width=600&height=400&seq=prod002&orientation=landscape" 
                  alt={t('product_trombone')} 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('product_trombone')}</h3>
                <p className="text-sm text-gray-600 mb-4">{t('product_trombone_desc')}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-amber-600 font-medium">{t('view_details')}</span>
                  <i className="ri-arrow-right-line text-amber-600"></i>
                </div>
              </div>
            </div>

            {/* Saxophone */}
            <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-full h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20golden%20alto%20saxophone%20standing%20upright%20on%20a%20clean%20white%20background%20with%20studio%20lighting%20highlighting%20the%20brass%20finish%20keys%20and%20bell%20product%20photography%20style&width=600&height=400&seq=prod003&orientation=landscape" 
                  alt={t('product_saxophone')} 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('product_saxophone')}</h3>
                <p className="text-sm text-gray-600 mb-4">{t('product_saxophone_desc')}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-amber-600 font-medium">{t('view_details')}</span>
                  <i className="ri-arrow-right-line text-amber-600"></i>
                </div>
              </div>
            </div>

            {/* French Horn */}
            <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-full h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20French%20horn%20brass%20instrument%20with%20circular%20coiled%20tubing%20on%20a%20clean%20white%20background%20studio%20lighting%20showcasing%20golden%20finish%20and%20valve%20details%20product%20photography%20style&width=600&height=400&seq=prod004&orientation=landscape" 
                  alt={t('product_horn')} 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('product_horn')}</h3>
                <p className="text-sm text-gray-600 mb-4">{t('product_horn_desc')}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-amber-600 font-medium">{t('view_details')}</span>
                  <i className="ri-arrow-right-line text-amber-600"></i>
                </div>
              </div>
            </div>

            {/* Clarinet */}
            <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-full h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20black%20clarinet%20woodwind%20instrument%20with%20silver%20keys%20on%20a%20clean%20white%20background%20studio%20lighting%20highlighting%20the%20ebony%20finish%20and%20key%20mechanism%20product%20photography%20style&width=600&height=400&seq=prod005&orientation=landscape" 
                  alt={t('product_clarinet')} 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('product_clarinet')}</h3>
                <p className="text-sm text-gray-600 mb-4">{t('product_clarinet_desc')}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-amber-600 font-medium">{t('view_details')}</span>
                  <i className="ri-arrow-right-line text-amber-600"></i>
                </div>
              </div>
            </div>

            {/* Flute */}
            <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-full h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20silver%20flute%20woodwind%20instrument%20laid%20horizontally%20on%20a%20clean%20white%20background%20studio%20lighting%20showcasing%20the%20polished%20metal%20finish%20and%20key%20details%20product%20photography%20style&width=600&height=400&seq=prod006&orientation=landscape" 
                  alt={t('product_flute')} 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('product_flute')}</h3>
                <p className="text-sm text-gray-600 mb-4">{t('product_flute_desc')}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-amber-600 font-medium">{t('view_details')}</span>
                  <i className="ri-arrow-right-line text-amber-600"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Product Catalog Download */}
          <div className="mt-16 bg-white rounded-lg shadow-md p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-pdf-2-line text-3xl text-amber-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{t('catalog_title')}</h3>
                  <p className="text-sm text-gray-600">{t('catalog_description')}</p>
                </div>
              </div>
              <a
                href="https://www.dropbox.com/scl/fi/zry69uvebvh92jykzmxw2/taishan-instruments-catalog.pdf?rlkey=ytbqbct2iidbtoli136ss6uso&st=8dqjmwvi&dl=1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-amber-600 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-download-2-line text-lg"></i>
                <span>{t('catalog_download')}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('about_title')}</h2>
            <p className="text-lg text-gray-600">{t('about_subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://static.readdy.ai/image/c1d369de78ba6b6a884b69ca21dbc9f2/9e76c62c8a3118e3c962c1a620cc1d33.png" 
                alt="泰山乐器公司" 
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('about_heading')}</h3>
                <p className="text-sm text-gray-700 leading-relaxed mb-4">
                  {t('about_p1')}
                </p>
                <p className="text-sm text-gray-700 leading-relaxed mb-4">
                  {t('about_p2')}
                </p>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {t('about_p3')}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6 pt-6">
                <div className="text-center p-6 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-amber-600 mb-2">1987</div>
                  <div className="text-sm text-gray-600">{t('about_year')}</div>
                </div>
                <div className="text-center p-6 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-amber-600 mb-2">30+</div>
                  <div className="text-sm text-gray-600">{t('about_series')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="mt-20">
            <h3 className="text-2xl font-bold text-gray-900 mb-12 text-center">{t('timeline_title')}</h3>
            <div className="space-y-8">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0 w-24 text-right">
                  <span className="text-lg font-bold text-amber-600">1987</span>
                </div>
                <div className="flex-shrink-0 w-3 h-3 bg-amber-600 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">{t('timeline_1987')}</p>
                </div>
              </div>
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0 w-24 text-right">
                  <span className="text-lg font-bold text-amber-600">2001</span>
                </div>
                <div className="flex-shrink-0 w-3 h-3 bg-amber-600 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">{t('timeline_2001')}</p>
                </div>
              </div>
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0 w-24 text-right">
                  <span className="text-lg font-bold text-amber-600">2004</span>
                </div>
                <div className="flex-shrink-0 w-3 h-3 bg-amber-600 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">{t('timeline_2004')}</p>
                </div>
              </div>
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0 w-24 text-right">
                  <span className="text-lg font-bold text-amber-600">2006</span>
                </div>
                <div className="flex-shrink-0 w-3 h-3 bg-amber-600 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">{t('timeline_2006')}</p>
                </div>
              </div>
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0 w-24 text-right">
                  <span className="text-lg font-bold text-amber-600">2013</span>
                </div>
                <div className="flex-shrink-0 w-3 h-3 bg-amber-600 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">{t('timeline_2013')}</p>
                </div>
              </div>
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0 w-24 text-right">
                  <span className="text-lg font-bold text-amber-600">2019</span>
                </div>
                <div className="flex-shrink-0 w-3 h-3 bg-amber-600 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">{t('timeline_2019')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('support_title')}</h2>
            <p className="text-lg text-gray-600">{t('support_subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{t('support_quality')}</h3>
              <p className="text-sm text-gray-600">{t('support_quality_desc')}</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-2-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{t('support_service')}</h3>
              <p className="text-sm text-gray-600">{t('support_service_desc')}</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-tools-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{t('support_maintenance')}</h3>
              <p className="text-sm text-gray-600">{t('support_maintenance_desc')}</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-book-open-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{t('support_guide')}</h3>
              <p className="text-sm text-gray-600">{t('support_guide_desc')}</p>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-20">
            <h3 className="text-2xl font-bold text-gray-900 mb-12 text-center">{t('faq_title')}</h3>
            <div className="max-w-3xl mx-auto space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="text-base font-bold text-gray-900 mb-2">{t('faq_q1')}</h4>
                <p className="text-sm text-gray-600">{t('faq_a1')}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="text-base font-bold text-gray-900 mb-2">{t('faq_q2')}</h4>
                <p className="text-sm text-gray-600">{t('faq_a2')}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="text-base font-bold text-gray-900 mb-2">{t('faq_q3')}</h4>
                <p className="text-sm text-gray-600">{t('faq_a3')}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="text-base font-bold text-gray-900 mb-2">{t('faq_q4')}</h4>
                <p className="text-sm text-gray-600">{t('faq_a4')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('contact_title')}</h2>
            <p className="text-lg text-gray-600">{t('contact_subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-6">{t('contact_info')}</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-amber-600"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-gray-900 mb-1">{t('contact_address')}</h4>
                    <p className="text-sm text-gray-600">{t('contact_address_value')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-amber-600"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-gray-900 mb-1">{t('contact_phone')}</h4>
                    <p className="text-sm text-gray-600">{t('contact_phone_value')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-xl text-green-600"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-base font-bold text-gray-900 mb-2">{t('contact_whatsapp')}</h4>
                    <p className="text-sm text-gray-600 mb-3">{t('contact_whatsapp_value')}</p>
                    <a
                      href="https://wa.me/6591791835"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-line text-lg"></i>
                      <span>{t('contact_whatsapp_button')}</span>
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-amber-600"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-gray-900 mb-1">{t('contact_email')}</h4>
                    <p className="text-sm text-gray-600">{t('contact_email_value')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-xl text-amber-600"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-gray-900 mb-1">{t('contact_hours')}</h4>
                    <p className="text-sm text-gray-600">{t('contact_hours_value')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-6">{t('contact_form_title')}</h3>
              <form 
                id="contact_form"
                data-readdy-form
                action="https://readdy.ai/api/form/d661hbdi8bkuedtp2bf0"
                method="POST"
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  
                  const name = formData.get('name') as string;
                  const email = formData.get('email') as string;
                  const message = formData.get('message') as string;
                  
                  if (!name || !email || !message) {
                    alert(t('contact_form_validate_required'));
                    return;
                  }
                  
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(email)) {
                    alert(t('contact_form_validate_email'));
                    return;
                  }
                  
                  if (message.length > 500) {
                    alert(t('contact_form_validate_message'));
                    return;
                  }
                  
                  fetch(form.action, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString(),
                  })
                    .then(response => {
                      if (response.ok) {
                        alert(t('contact_form_success'));
                        form.reset();
                      } else {
                        alert(t('contact_form_error'));
                      }
                    })
                    .catch(() => {
                      alert(t('contact_form_error'));
                    });
                }}
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact_form_name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder={t('contact_form_name_placeholder')}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact_form_email')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder={t('contact_form_email_placeholder')}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact_form_phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder={t('contact_form_phone_placeholder')}
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact_form_message')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                    placeholder={t('contact_form_message_placeholder')}
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-amber-600 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('contact_form_submit')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-amber-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/c1d369de78ba6b6a884b69ca21dbc9f2/81a4a68f1cccee589378da1e71db4154.png" 
                alt="泰山乐器" 
                className="h-12 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-sm text-white/90">
                {t('footer_description')}
              </p>
            </div>
            <div>
              <h4 className="text-base font-bold mb-4">{t('footer_quick_links')}</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('home')} className="text-sm text-white/90 hover:text-white transition-colors cursor-pointer">
                    {t('nav_home')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('products')} className="text-sm text-white/90 hover:text-white transition-colors cursor-pointer">
                    {t('nav_products')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-sm text-white/90 hover:text-white transition-colors cursor-pointer">
                    {t('nav_about')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('support')} className="text-sm text-white/90 hover:text-white transition-colors cursor-pointer">
                    {t('nav_support')}
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-bold mb-4">{t('footer_contact_us')}</h4>
              <ul className="space-y-2">
                <li className="text-sm text-white/90">{t('footer_address')}</li>
                <li className="text-sm text-white/90">{t('footer_hours')}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 text-center">
            <p className="text-sm text-white/80">
              &copy; 2024 {t('footer_copyright')} | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">{t('footer_powered')}</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
